import type { Store } from 'effector'

import { combine } from 'effector'
import { $t } from './main'

/**
 * Cache for simple (just i18n keys) translations
 */
const cache = new Map<string, Store<string>>()

function translatedWithVariables(
  key: string,
  variables?: { [name: string]: unknown }
): Store<string> {
  // use simple caching for simple translations
  if (variables == null) {
    let $store = cache.get(key)
    if ($store == null) {
      $store = $t.map(({ t }) => t(key))
      cache.set(key, $store)
    }
    return $store
  }

  // if variables are provided -> create a new store
  return combine(
    { $t, variables: combine(variables ?? {}) },
    ({ $t: { t }, variables }) => t(key, variables)
  )
}

function translateObject(shape: {
  [key: string]: string | [string, { [name: string]: unknown }]
}): { [key: string]: Store<string> } {
  const result: { [key: string]: Store<string> } = {}
  for (const [key, value] of Object.entries(shape)) {
    if (Array.isArray(value)) {
      result[key] = translatedWithVariables(...value)
    } else {
      result[key] = translatedWithVariables(value)
    }
  }
  return result
}

function translatedLiteral(
  parts: TemplateStringsArray,
  ...stores: unknown[]
): Store<string> {
  // use simple caching for simple translations
  if (parts.length === 1 && stores.length === 0) {
    const key = parts[0]
    let $store = cache.get(key)
    if ($store == null) {
      $store = $t.map(({ t }) => t(key))
      cache.set(key, $store)
    }
    return $store
  }

  // if variables are provided -> create a new store
  return combine(
    { $t, dynamicParts: combine(stores) },
    ({ $t: { t }, dynamicParts }) => {
      const result: string[] = []

      parts.forEach((part, i) => {
        const resolved = dynamicParts[i]
        result.push(part, resolved ? String(resolved) : '')
      })

      const key = result.join('')
      return t(key)
    }
  )
}

export function translated(
  key: string,
  variables?: { [name: string]: unknown }
): Store<string>

export function translated(
  parts: TemplateStringsArray,
  ...stores: unknown[]
): Store<string>

export function translated(shape: {
  [key: string]: string | [string, { [name: string]: unknown }]
}): { [key: string]: Store<string> }

export function translated(
  firstArg:
    | string
    | TemplateStringsArray
    | { [key: string]: string | [string, Record<string, unknown>] },

  // literally anything can be passed as rest arguments
  ...args: any[]
) {
  if (Array.isArray(firstArg)) {
    return translatedLiteral(firstArg as TemplateStringsArray, ...args)
  }

  if (firstArg != null && typeof firstArg === 'object') {
    // this is object, it is just look ugly to specify type here
    return translateObject(firstArg as any)
  }

  return translatedWithVariables(String(firstArg), args[0])
}
